import { classMerge } from '@components/utilities/classMerge'
import zIndices from '@components/utilities/zIndex'
import type {
  BackgroundColor,
  BorderColor,
  TextColor,
} from '@lib/colors/colors'
import type { ReactNode } from 'react'
import Select, {
  type CSSObjectWithLabel,
  type MenuPlacement,
  type StylesConfig,
} from 'react-select'

export type LabelSelectOption = {
  backgroundColor?: BackgroundColor
  borderColor?: BorderColor
  color?: TextColor
  label: string | ReactNode
  value?: string
}

type LabelSelectBoxProps = {
  className?: string
  customContainerStyles?: CSSObjectWithLabel
  customMenuStyles?: CSSObjectWithLabel
  defaultBackgroundColor?: BackgroundColor
  disabled?: boolean
  instanceId: string | number
  isLoading?: boolean
  items: LabelSelectOption[]
  maxMenuHeight?: number
  menuClassNames?: string
  menuPlacement?: MenuPlacement
  minimized?: boolean
  onChange: (value?: string) => void
  optionClassNames?: string
  pill?: boolean
  placeholder?: string
  value?: string
  valueContainerClassNames?: string
}

export default function LabelSelectBox({
  className,
  customContainerStyles,
  customMenuStyles,
  defaultBackgroundColor = 'bg-neutral50',
  disabled = false,
  instanceId,
  isLoading,
  items,
  maxMenuHeight,
  menuClassNames,
  menuPlacement = 'auto',
  minimized = false,
  onChange,
  optionClassNames,
  pill = false,
  placeholder,
  value,
  valueContainerClassNames,
}: LabelSelectBoxProps) {
  const selectedItem = items.find((item) => item.value === value)

  const selectedValue = selectedItem
    ? {
        label: selectedItem.label,
        value: selectedItem.value,
      }
    : null

  const color = selectedItem?.color ?? 'text-neutral600'
  const backgroundColor =
    selectedItem?.backgroundColor ?? defaultBackgroundColor
  const borderColor = selectedItem?.borderColor ?? 'border-neutral400'

  const selectOptions = items.map((item) => ({
    label: item.label,
    value: item.value,
  }))

  const customClassNames = {
    container: () => 'pr-2 cursor-pointer',
    dropdownIndicator: disabled ? () => '!hidden' : undefined,
    indicatorSeparator: disabled ? () => '!hidden' : undefined,
    menu: () =>
      menuClassNames ?? 'cursor-pointer mt-1 border border-neutral600',
    option: () =>
      optionClassNames ??
      `p-2 hover:cursor-pointer hover:bg-white ${backgroundColor}`,
    valueContainer: () => valueContainerClassNames ?? 'pl-4 cursor-pointer',
  }

  const customStyles: StylesConfig<LabelSelectOption, false> = {
    container: (provided: CSSObjectWithLabel) => ({
      ...provided,
      ...customContainerStyles,
    }),
    menu: (provided: CSSObjectWithLabel) => ({
      ...provided,
      ...customMenuStyles,
      zIndex: parseInt(zIndices.labelSelectBoxMenuPopOver),
    }),
  }
  return (
    <Select
      className={classMerge(
        'w-full',
        'font-semibold',
        borderColor,
        backgroundColor,
        color,
        className,
        {
          'py-3': !minimized,
          'rounded-full': pill,
        },
      )}
      classNames={customClassNames}
      instanceId={instanceId}
      isDisabled={disabled}
      isLoading={isLoading}
      isMulti={false}
      isSearchable={false}
      maxMenuHeight={maxMenuHeight}
      menuPlacement={menuPlacement}
      onChange={(selectedOption) => onChange(selectedOption?.value)}
      options={selectOptions}
      placeholder={placeholder}
      styles={customStyles}
      unstyled
      value={selectedValue}
    />
  )
}
